import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Only the fallback locale ships in the initial bundle. Every other locale is
// code-split and fetched on demand, which keeps ~2.8MB of JSON out of the
// critical path for the first paint.
import enUS from './i18n/locales/en-US.json';

// Five languages, no country variants. en-GB, en-SG, en-ZA and fr-CA were
// retired on 30 August 2026: they carried the same English or French copy as
// their parent and existed only to attach a country flag and a currency.
// The codes stay region-tagged because they are the URL segment and every
// indexed localized page uses them; the switcher presents language only.
export const SUPPORTED_LOCALES = [
  'en-US', 'fr-FR', 'es-MX', 'de-DE', 'ja-JP'
] as const;

export type SupportedLocale = typeof SUPPORTED_LOCALES[number];

export const LOCALE_NAMES: Record<SupportedLocale, string> = {
  'en-US': 'English',
  'fr-FR': 'Français',
  'es-MX': 'Español',
  'de-DE': 'Deutsch',
  'ja-JP': '日本語'
};

type LazyLocale = Exclude<SupportedLocale, 'en-US'>;

const localeLoaders: Record<LazyLocale, () => Promise<{ default: Record<string, unknown> }>> = {
  'fr-FR': () => import('./i18n/locales/fr-FR.json'),
  'es-MX': () => import('./i18n/locales/es-MX.json'),
  'de-DE': () => import('./i18n/locales/de-DE.json'),
  'ja-JP': () => import('./i18n/locales/ja-JP.json'),
};

const inFlight = new Map<string, Promise<void>>();

const isSupported = (value: string): value is SupportedLocale =>
  (SUPPORTED_LOCALES as readonly string[]).includes(value);

/**
 * Fetches and registers a locale's translation bundle. Safe to call repeatedly:
 * already-loaded locales resolve immediately.
 */
export const ensureLocaleLoaded = (locale: string): Promise<void> => {
  if (!isSupported(locale) || locale === 'en-US') return Promise.resolve();
  if (i18n.hasResourceBundle(locale, 'translation')) return Promise.resolve();

  const existing = inFlight.get(locale);
  if (existing) return existing;

  const request = localeLoaders[locale]()
    .then((mod) => {
      i18n.addResourceBundle(locale, 'translation', mod.default, true, true);
    })
    .catch((error) => {
      // Fall back to en-US rather than blocking the render.
      console.error(`[i18n] Failed to load locale "${locale}"`, error);
    })
    .finally(() => {
      inFlight.delete(locale);
    });

  inFlight.set(locale, request);
  return request;
};

/** Reads the locale from the first path segment, e.g. /fr-FR/pricing. */
export const getLocaleFromPath = (pathname: string): SupportedLocale => {
  const segment = pathname.split('/')[1] ?? '';
  return isSupported(segment) ? segment : 'en-US';
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next);

// Dev-only inspector: records missing / English-fallback translations so the
// on-screen overlay can highlight them. Stripped from production builds.
const devPostProcess = import.meta.env.DEV ? ['i18nDevInspect'] : undefined;
if (import.meta.env.DEV) {
  void import('./dev/i18nDevInspector').then(({ createI18nDevInspector }) => {
    i18n.use(createI18nDevInspector(i18n));
  });
}

i18n.init({
    resources: {
      'en-US': { translation: enUS },
    },
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    fallbackLng: 'en-US',
    debug: false,
    ...(devPostProcess ? { postProcess: devPostProcess } : {}),

    detection: {
      order: ['path', 'localStorage', 'navigator', 'htmlTag'],
      lookupFromPathIndex: 0,
      lookupFromSubdomainIndex: 0,
      caches: ['localStorage']
    },

    interpolation: {
      escapeValue: false
    }
  });

export default i18n;
